import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  Grid,
  Card,
  CardContent,
  IconButton,
  TextField,
  Stack,
  Divider,
  Tooltip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const ResearchReviewMock = ({ onNext, onBack, criteria, enrichmentColumns, uploadedFile }) => {
  const [previewData, setPreviewData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [qualificationStats, setQualificationStats] = useState({
    total: 20,
    qualified: 15,
    disqualified: 5,
  });
  const [editableCriteria, setEditableCriteria] = useState({
    mustHaves: 'Must be a B2B SaaS company\nMust have raised funding\nMust have 20+ employees',
    niceToHaves: 'Preferably based in the US\nIdeally uses modern tech stack\nBonus if they have API documentation',
    disqualifications: 'Exclude consulting firms\nNo agencies or freelancers'
  });

  // Generate mock preview data (10-20 rows as per vision)
  useEffect(() => {
    generatePreview();
  }, [editableCriteria]);

  const generatePreview = () => {
    setLoading(true);
    setTimeout(() => {
      // Generate sample rows showing preview as described in vision
      const samples = Array.from({ length: 15 }, (_, i) => {
        const qualified = Math.random() > 0.25; // ~75% qualification rate for demo
        const score = qualified ? Math.floor(Math.random() * 5) + 1 : 0;
        
        return {
          id: i + 1,
          website: `https://example${i + 1}.com`,
          companyName: `Example Company ${i + 1}`,
          qualified,
          score,
          rating: getRating(score),
          reasoning: qualified 
            ? `Meets all must-have criteria. ${score > 3 ? 'Strong match on nice-to-haves.' : 'Basic qualification.'}`
            : 'Failed: Not a B2B SaaS company or insufficient funding',
          enrichedData: enrichmentColumns?.reduce((acc, col) => {
            acc[col.name] = generateMockEnrichedValue(col);
            return acc;
          }, {}),
        };
      });

      setPreviewData(samples);
      
      const stats = {
        qualified: samples.filter(s => s.qualified).length,
        disqualified: samples.filter(s => !s.qualified).length,
        total: samples.length,
      };
      setQualificationStats(stats);
      setLoading(false);
    }, 800);
  };

  const getRating = (score) => {
    if (score === 0) return { label: 'Disqualified', color: 'error' };
    if (score === 1) return { label: 'Acceptable', color: 'warning' };
    if (score <= 4) return { label: 'Good', color: 'info' };
    return { label: 'Excellent', color: 'success' };
  };

  const generateMockEnrichedValue = (column) => {
    const mockData = {
      text: ['Technology', 'Finance', 'Healthcare', 'Retail'][Math.floor(Math.random() * 4)],
      number: Math.floor(Math.random() * 1000) + 50,
      currency: `$${Math.floor(Math.random() * 100)}M`,
      date: '2024',
      boolean: Math.random() > 0.5 ? 'Yes' : 'No',
    };
    return mockData[column.dataFormat] || 'N/A';
  };

  const calculateCosts = () => {
    const totalWebsites = uploadedFile?.rowCount || 500;
    const baseAnalysis = totalWebsites * 1; // 1 credit per website
    const enrichmentCost = enrichmentColumns?.reduce(
      (sum, col) => sum + (col.useWebSearch ? 3 : 1) * totalWebsites,
      0
    ) || 0;
    
    return {
      baseAnalysis,
      enrichmentCost,
      total: baseAnalysis + enrichmentCost,
      estimatedDollars: ((baseAnalysis + enrichmentCost) * 0.0035).toFixed(2),
    };
  };

  const costs = calculateCosts();

  return (
    <Box sx={{ width: '100%' }}>
      {/* STEP 3 Header matching List Cleaner */}
      <Box sx={{ mb: 3 }}>
        <Typography 
          variant="h6" 
          component="h1" 
          gutterBottom
          sx={{ 
            textTransform: 'uppercase',
            letterSpacing: '0.15em',
            fontWeight: 500,
            fontSize: '0.875rem',
            color: '#FFFFFF'
          }}
        >
          STEP 3: REVIEW & CONFIRM
        </Typography>
      </Box>

      {/* 50/50 Layout: Sample Preview on left, Interactive Refinement + Credit Estimation on right */}
      <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
        
        {/* Left side: Sample Data Preview */}
        <Box sx={{ flex: 1 }}>
          <Paper variant="outlined" sx={{ p: 3, bgcolor: '#161c3e', borderColor: 'rgba(255, 255, 255, 0.1)', mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ color: '#FFFFFF' }}>
                Sample Preview
              </Typography>
              <Button
                startIcon={<RefreshIcon />}
                onClick={generatePreview}
                size="small"
                sx={{ 
                  color: 'text.secondary',
                  textTransform: 'none'
                }}
              >
                Refresh Preview
              </Button>
            </Box>

            <Alert severity="info" sx={{ mb: 2 }}>
              Showing {previewData.length} randomly selected rows from your uploaded file (processed client-side, no API costs)
            </Alert>

            {/* Live Qualification Counter */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={4}>
                <Card sx={{ 
                  bgcolor: 'rgba(250, 163, 44, 0.1)', 
                  border: '1px solid rgba(250, 163, 44, 0.3)',
                  textAlign: 'center',
                  py: 1
                }}>
                  <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                    <Typography variant="h5" sx={{ color: '#faa32c', fontWeight: 'bold' }}>
                      {qualificationStats.total}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Total
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={4}>
                <Card sx={{ 
                  bgcolor: 'rgba(0, 149, 255, 0.1)', 
                  border: '1px solid rgba(0, 149, 255, 0.3)',
                  textAlign: 'center',
                  py: 1
                }}>
                  <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                    <Typography variant="h5" sx={{ color: '#0095FF', fontWeight: 'bold' }}>
                      {qualificationStats.qualified}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Qualify
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={4}>
                <Card sx={{ 
                  bgcolor: 'rgba(232, 62, 140, 0.1)', 
                  border: '1px solid rgba(232, 62, 140, 0.3)',
                  textAlign: 'center',
                  py: 1
                }}>
                  <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                    <Typography variant="h5" sx={{ color: '#E83E8C', fontWeight: 'bold' }}>
                      {qualificationStats.disqualified}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Exclude
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Sample Results Table */}
            <TableContainer component={Paper} sx={{ bgcolor: 'rgba(255, 255, 255, 0.02)', maxHeight: 400, overflow: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: '#FFFFFF', fontWeight: 'medium', fontSize: '0.75rem' }}>Website</TableCell>
                    <TableCell sx={{ color: '#FFFFFF', fontWeight: 'medium', fontSize: '0.75rem' }}>Rating</TableCell>
                    <TableCell sx={{ color: '#FFFFFF', fontWeight: 'medium', fontSize: '0.75rem' }}>Preview</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={3} sx={{ textAlign: 'center', py: 3 }}>
                        <Typography color="textSecondary">Generating preview...</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    previewData.slice(0, 8).map((row) => (
                      <TableRow key={row.id}>
                        <TableCell sx={{ color: '#FFFFFF', fontSize: '0.75rem' }}>
                          {row.website}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={row.rating.label}
                            size="small"
                            color={row.rating.color}
                            sx={{ fontSize: '0.7rem', height: 20 }}
                          />
                        </TableCell>
                        <TableCell sx={{ color: 'text.secondary', fontSize: '0.7rem', maxWidth: 200 }}>
                          <Typography variant="caption" noWrap>
                            {row.reasoning}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>

        {/* Right side: Interactive Refinement + Credit Estimation */}
        <Box sx={{ flex: 1 }}>
          
          {/* Interactive Refinement Section */}
          <Paper variant="outlined" sx={{ p: 3, bgcolor: '#161c3e', borderColor: 'rgba(255, 255, 255, 0.1)', mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#FFFFFF' }}>
              Interactive Refinement
            </Typography>
            
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Edit criteria in-place and see live updates to qualification counts
            </Typography>

            {/* Must-Haves */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ color: '#E83E8C', mb: 1 }}>
                Must-Have Criteria
                <IconButton size="small" sx={{ ml: 1, color: 'text.secondary' }}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                size="small"
                value={editableCriteria.mustHaves}
                onChange={(e) => setEditableCriteria({...editableCriteria, mustHaves: e.target.value})}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: '0.8rem',
                    '& fieldset': {
                      borderColor: 'rgba(232, 62, 140, 0.3)',
                    },
                  },
                }}
              />
            </Box>

            {/* Nice-to-Haves */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ color: '#0095FF', mb: 1 }}>
                Nice-to-Have Criteria
                <IconButton size="small" sx={{ ml: 1, color: 'text.secondary' }}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                size="small"
                value={editableCriteria.niceToHaves}
                onChange={(e) => setEditableCriteria({...editableCriteria, niceToHaves: e.target.value})}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: '0.8rem',
                    '& fieldset': {
                      borderColor: 'rgba(0, 149, 255, 0.3)',
                    },
                  },
                }}
              />
            </Box>

            {/* Disqualifications */}
            <Box>
              <Typography variant="subtitle2" sx={{ color: '#faa32c', mb: 1 }}>
                Disqualification Criteria
                <IconButton size="small" sx={{ ml: 1, color: 'text.secondary' }}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={2}
                size="small"
                value={editableCriteria.disqualifications}
                onChange={(e) => setEditableCriteria({...editableCriteria, disqualifications: e.target.value})}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: '0.8rem',
                    '& fieldset': {
                      borderColor: 'rgba(250, 163, 44, 0.3)',
                    },
                  },
                }}
              />
            </Box>
          </Paper>

          {/* Credit Estimation Section */}
          <Paper variant="outlined" sx={{ p: 3, bgcolor: '#161c3e', borderColor: 'rgba(255, 255, 255, 0.1)' }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#FFFFFF', display: 'flex', alignItems: 'center' }}>
              <AttachMoneyIcon sx={{ mr: 1 }} />
              Credit Estimation
            </Typography>
            
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="textSecondary">
                  Base analysis: {uploadedFile?.rowCount || 500} websites × 1 credit
                </Typography>
                <Typography variant="body2" sx={{ color: '#FFFFFF' }}>
                  {uploadedFile?.rowCount || 500} credits
                </Typography>
              </Box>
              
              {enrichmentColumns?.length > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="textSecondary">
                    Data enrichment: {enrichmentColumns.length} columns
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#FFFFFF' }}>
                    {enrichmentColumns.reduce((sum, col) => sum + (col.useWebSearch ? 3 : 1) * (uploadedFile?.rowCount || 500), 0)} credits
                  </Typography>
                </Box>
              )}
              
              <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)' }} />
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="h6" sx={{ color: '#FFFFFF' }}>
                  Total Estimated Cost
                </Typography>
                <Typography variant="h6" sx={{ color: '#faa32c', fontWeight: 'bold' }}>
                  {calculateCosts().total} credits
                </Typography>
              </Box>
              
              <Typography variant="caption" color="textSecondary">
                ≈ ${calculateCosts().estimatedDollars} USD at current rates
              </Typography>
            </Stack>
          </Paper>
        </Box>
      </Box>

      {/* Navigation Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button 
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          variant="outlined" 
          sx={{ 
            borderColor: 'rgba(255,255,255,0.3)',
            color: 'text.secondary',
            textTransform: 'none',
            borderRadius: '4px'  
          }}
        >
          Back to Criteria
        </Button>
        
        <Button 
          variant="contained" 
          color="primary"
          onClick={onNext} 
          sx={{ 
            minWidth: 140,
            textTransform: 'none', 
            borderRadius: '4px',
            fontWeight: 'medium',
            px: 4
          }}
        >
          Start Research
        </Button>
      </Box>
    </Box>
  );
};

export default ResearchReviewMock;